// @ts-nocheck

import './Select.scss'
import React, {
    ReactNode,
    useCallback,
    useRef,
    useEffect,
    createContext,
    useContext,
    useLayoutEffect,
    useState,
} from "react";
import ReactDOM from "react-dom";
import { DropdownArrow, LoaderSvg } from "../svgs/Svgs";
import { mergeClassNames } from "../../../utils/commonFunctions";
import { ContentProps, ItemProps, SelectContextType, SelectProps, TriggerProps, } from "./types";
import useSelectState from './hooks/useSelectState'
import useClickOutside from './hooks/useClickOutside'
import useKeyboardNavigation from './hooks/useKeyboardNavigation'
import { computeDropdownPosition } from './hooks/useDropdownPosition'
import CustomToolTip from '../CustomToolTip';

const SelectContext = createContext<SelectContextType | undefined>(undefined);

const useSelect = () => {
    const context = useContext(SelectContext);
    if (!context)
        throw new Error("All Select subcomponents must be inside Select");
    return context;
};

/**
 * A dropdown select component that provides a modern and interactive way to select options.
 * It supports single value selection, search, and custom actions.
 *
 * @example
 * <Select value="Ethereum" options={['Bitcoin', 'Ethereum', 'Solana']} onSelect={(v) => console.log(v)} />
 *
 * @param {ReactNode} children - The content of the select component.
 * @param {string} className - The CSS class name for the select component.
 * @param {"bottom" | "top" | "auto"} placement - The placement of the dropdown menu.
 * @param {number} id - The unique ID for the select component.
 * @param {string | null} value - The current selected value.
 * @param {(value: string | null) => void} onSelect - The callback function when a value is selected.
 * @param {(value: boolean) => void} onOpenChange - The callback function when the dropdown menu is opened or closed.
 *
 * @typedef {object} SelectContextType
 * @property {string | null} selected - The current selected value.
 * @property {(value: string | null) => void} onSelectChange - The callback function when a value is selected.
 * @property {boolean} open - Whether the dropdown menu is open or not.
 * @property {(value: boolean) => void} setOpen - The callback function when the dropdown menu is opened or closed.
 * @property {React.RefObject<HTMLButtonElement>} triggerRef - The reference to the trigger button element.
 * @property {"bottom" | "top" | "auto"} placement - The placement of the dropdown menu.
 * @property {{ top: number; left: number }} coords - The coordinates of the dropdown menu.
 * @property {Dispatch<SetStateAction<{ top: number; left: number }>>} setCoords - The callback function to set the coordinates of the dropdown menu.
 * @property {"bottom" | "top"} resolvedPlacement - The resolved placement of the dropdown menu.
 * @property {Dispatch<SetStateAction<"bottom" | "top">>} setResolvedPlacement - The callback function to set the resolved placement of the dropdown menu.
 *
 * @typedef {React.FC<SelectProps>} SelectTrigger - The trigger component of the select component.
 * @typedef {React.FC<SelectProps>} SelectValue - The value component of the select component.
 * @typedef {React.FC<SelectProps>} SelectContent - The content component of the select component.
 * @typedef {React.FC<SelectProps>} SelectGroup - The group component of the select component.
 * @typedef {React.FC<SelectProps>} SelectGroupHeader - The group header component of the select component.
 * @typedef {React.FC<SelectProps>} SelectGroupDevider - The group provider component of the select component.
 * @typedef {React.FC<SelectProps>} SelectGroupLabel - The group label component of the select component.
 * @typedef {React.FC<SelectProps>} SelectItem - The item component of the select component.
*/

export const Select: React.FC<SelectProps> & {
    Trigger: typeof Trigger;
    Value: typeof Value;
    Content: typeof Content;
    Group: typeof Group;
    GroupHeader: typeof GroupHeader;
    GroupDivider: typeof GroupDivider;
    GroupLabel: typeof GroupLabel;
    Item: typeof Item;
} = ({ children, className = '', placement = "auto", id, value, onSelect, onOpenChange, multiselect = false, style, searchable = false, onSearchChange }) => {
    // centralised state and actions
    const {
        selected,
        setSelected,
        open,
        setOpen,
        activeItemIndex,
        setActiveItemIndex,
        itemCount,
        setItemCount,
        reset,
    } = useSelectState(value, id, onOpenChange, onSelect as any);

    const [coords, setCoords] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 })
    const [resolvedPlacement, setResolvedPlacement] = React.useState<"bottom" | "top" | "auto">('bottom')
    const [searchQuery, setSearchQuery] = useState('');
    const triggerRef = useRef<HTMLButtonElement | HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    // Reset search when dropdown closes
    useEffect(() => {
        if (!open) setSearchQuery('');
    }, [open]);

    // Notify consumer of search query changes
    useEffect(() => {
        onSearchChange?.(searchQuery);
    }, [searchQuery]);


    // click outside (uses setOpen via hook)
    useClickOutside(triggerRef, contentRef, open, reset)

    // keyboard navigation: pass a callback that triggers the option element click by index
    useKeyboardNavigation(open, itemCount, activeItemIndex, setOpen, setActiveItemIndex, (index) => {
        if (!contentRef.current) return
        const options = contentRef.current.querySelectorAll('[role="option"]') as NodeListOf<HTMLElement>
        const el = options[index]
        if (el) el.click()
    })

    const onSelectChange = useCallback((val: string | null) => {
        // ensure select state and notify consumer
        setSelected(val)
        if (!multiselect) {
            setOpen(false);
        }
    }, [setSelected, setOpen, multiselect])

    const contextValue = React.useMemo(() => ({
        selected,
        onSelectChange,
        open,
        setOpen,
        triggerRef,
        contentRef,
        placement,
        coords,
        setCoords,
        resolvedPlacement,
        setResolvedPlacement,
        activeItemIndex,
        setActiveItemIndex,
        itemCount,
        setItemCount,
        multiselect,
        searchable,
        searchQuery,
        setSearchQuery,
    }), [
        selected,
        setSelected,
        open,
        setOpen,
        triggerRef,
        contentRef,
        placement,
        coords,
        setCoords,
        resolvedPlacement,
        setResolvedPlacement,
        activeItemIndex,
        setActiveItemIndex,
        itemCount,
        setItemCount,
        multiselect,
        searchable,
        searchQuery,
        setSearchQuery,
    ]);

    return (
        <SelectContext.Provider value={contextValue}>
            <div
                className={mergeClassNames(`custom__select__container ${open && `select-${resolvedPlacement}`}`, className)}
                role="combobox"
                aria-expanded={open}
                aria-haspopup="listbox"
                style={style}
            >
                {children}
            </div>
        </SelectContext.Provider>
    );
};

// ---------- SUB COMPONENTS ---------- //

const Trigger: React.FC<TriggerProps> = ({
    children,
    className = '',
    icon,
    hideDropdownIcon = false,
    iconPosition = "right",
    disabled,
    loading,
    ...props
}) => {
    const { open, setOpen, triggerRef, searchable } = useSelect();

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(!open);
        } else if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
        }
    };

    const renderIcon = () => {
        if (hideDropdownIcon) return null;
        if (icon) return <div className="select__trigger__icon">{icon}</div>;
        if (loading) return (
            <div className="select__trigger__icon">
                <LoaderSvg />
            </div>
        );
        return <div className="select__trigger__icon"><DropdownArrow /></div>;
    };

    const content = (
        <>
            {iconPosition === "left" && renderIcon()}
            {children}
            {iconPosition === "right" && renderIcon()}
        </>
    );

    // When searchable, render a div so the input inside is valid HTML
    if (searchable) {
        return (
            <div
                ref={triggerRef as React.MutableRefObject<HTMLDivElement>}
                className={mergeClassNames(`select__trigger`, className, disabled ? 'disabled' : '')}
                onClick={() => { if (!disabled) setOpen(!open); }}
                aria-label="Select trigger"
                aria-controls="select-listbox"
                aria-expanded={open}
            >
                {content}
            </div>
        );
    }

    return (
        <button
            type="button"
            disabled={disabled}
            ref={triggerRef as React.MutableRefObject<HTMLButtonElement>}
            className={mergeClassNames(`select__trigger`, className)}
            onClick={() => setOpen(!open)}
            onKeyDown={handleKeyDown}
            aria-label="Select trigger"
            aria-controls="select-listbox"
            aria-expanded={open}
            {...props}
        >
            {content}
        </button>
    );
};

const Value: React.FC<{ placeholder?: string; value?: string }> = ({ placeholder, value }) => {
    const { selected, open, searchable, searchQuery, setSearchQuery, setOpen } = useSelect();

    if (searchable) {
        const displayValue = open ? searchQuery : (value || selected || '');
        const displayPlaceholder = open ? (value || selected || placeholder || '') : placeholder;
        return (
            <input
                className="control__selected__value select__search__input"
                value={displayValue}
                placeholder={displayPlaceholder}
                autoFocus={open}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => {
                    e.stopPropagation();
                    if (!open) setOpen(true);
                }}
            />
        );
    }

    return (
        <span className="control__selected__value">
            {value || selected || <span className="select-placeholder">{placeholder}</span>}
        </span>
    );
};


const Content: React.FC<ContentProps> = ({ children, className = "" }) => {
    const {
        open,
        triggerRef,
        contentRef,
        placement,
        resolvedPlacement,
        setResolvedPlacement,
        coords,
        setCoords,
        itemCount,
    } = useSelect();

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const content = contentRef.current;
        if (!trigger || !content) return;

        const triggerRect = trigger.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();

        const { top, left, placement: finalPlacement } = computeDropdownPosition(
            triggerRect,
            contentRect,
            placement
        );

        setCoords({ top, left });
        setResolvedPlacement(finalPlacement);

        content.style.position = "fixed";
        content.style.top = `${top}px`;
        content.style.left = `${left}px`;
        content.style.width = `${trigger.offsetWidth}px`;
    }, [triggerRef, contentRef, placement, setCoords, setResolvedPlacement]);

    // Reposition when opening
    useLayoutEffect(() => {
        if (!open) return;

        // First paint
        updatePosition();
        requestAnimationFrame(updatePosition);
    }, [open, updatePosition]);

    // Window resize + scroll reposition
    useEffect(() => {
        if (!open) return;

        const onResize = () => updatePosition();
        const onScroll = () => updatePosition();

        window.addEventListener("resize", onResize);
        window.addEventListener("scroll", onScroll, true);

        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("scroll", onScroll, true);
        };
    }, [open, updatePosition]);

    // Simple assignment (NO read-only error)
    const setContentRef = (el: HTMLDivElement | null) => {
        (contentRef as any).current = el;
    };

    if (!open) return null;

    return ReactDOM.createPortal(
        <div
            ref={setContentRef}
            className={mergeClassNames(
                `select__option__container select-${resolvedPlacement}`,
                className
            )}
            style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
                minWidth: triggerRef.current?.offsetWidth,
            }}
        >
            {children}
            {itemCount === 0 && (
                <div className="select__empty">No options</div>
            )}
        </div>,
        document.body
    );
};



const Group: React.FC<{ children: ReactNode; className?: string }> = ({
    children,
    className = '',
}) => <div className={mergeClassNames('select__group__container', className)}>{children}</div>;


const GroupDivider: React.FC<{ className?: string }> = ({ className = '' }) => (
    <div role="separator" className={mergeClassNames('select__group__devider', className)} />
);


const GroupHeader: React.FC<{ children: ReactNode; className?: string }> = ({
    children,
    className = '',
}) => <div className={mergeClassNames('group__header', className)}>{children}</div>;

const GroupLabel: React.FC<{ children: ReactNode; className?: string }> = ({
    children,
    className = '',
}) => <div className={mergeClassNames('group__label', className)}>{children}</div>;


const Item: React.FC<ItemProps> = ({ value, children, className = '' }) => {
    const { onSelectChange, setOpen, selected, activeItemIndex, setActiveItemIndex, itemCount, setItemCount, multiselect } = useSelect();
    const itemRef = useRef<HTMLDivElement>(null);
    const itemIndexRef = useRef<number>(-1);

    // Register this item's index before paint to avoid flash of empty state
    useLayoutEffect(() => {
        setItemCount(prev => {
            itemIndexRef.current = prev;
            return prev + 1;
        });
        return () => {
            setItemCount(prev => Math.max(0, prev - 1));
        };
    }, [setItemCount]);

    // Scroll into view when focused (only for single select)
    useEffect(() => {
        if (!multiselect && activeItemIndex > 0 && activeItemIndex === itemIndexRef.current && itemRef.current) {
            itemRef.current.scrollIntoView({ block: "nearest" });
        }
    }, [activeItemIndex, multiselect]);


    const handleSelect = () => {
        onSelectChange(value);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelect();
        }
    };

    const handleMouseEnter = () => {
        setActiveItemIndex(itemIndexRef.current);
    };

    const isFocused = activeItemIndex === itemIndexRef.current;

    return (
        <CustomToolTip title={typeof children === 'string' ? children : ''} placement='right'>
            <div
                ref={itemRef}
                role="option"
                aria-selected={selected === value}
                tabIndex={isFocused ? 0 : -1}
                className={mergeClassNames(
                    `select__option ${selected === value && "selected"}`,
                    className
                )}
                onClick={handleSelect}
                onKeyDown={handleKeyDown}
                onMouseEnter={handleMouseEnter}
            >
                {children}
            </div>
        </CustomToolTip>
    );
};

// attach subcomponents
Select.Trigger = Trigger;
Select.Value = Value;
Select.Content = Content;
Select.Group = Group;
Select.GroupHeader = GroupHeader;
Select.GroupDivider = GroupDivider;
Select.Item = Item;
Select.GroupLabel = GroupLabel;

export default Select;
